import React, { useCallback } from "react";
import {
  Auth0ContextInterface,
  Auth0Provider,
  LogoutOptions,
  useAuth0,
  withAuthenticationRequired
} from "@auth0/auth0-react";
import { useEnv } from "store/env";
import { parse } from "polyrouter";
import { History } from "history";
import { removeBetaNotificationFromLocalStorage } from "utilities/betaNotificationUtils";

//The proper type is not exported from the auth- package.
type WithAuthenticationRequiredOptions = Parameters<
  typeof withAuthenticationRequired
>[1];
type AuthProviderProps = {
  history: History;
  children: React.ReactNode;
};

const LogoutAction = (
  options: LogoutOptions | undefined,
  logout: (options?: LogoutOptions | undefined) => void
) => {
  removeBetaNotificationFromLocalStorage();
  return logout({
    returnTo: window.location.origin,
    federated: true,
    ...options
  });
};

export const useLogout = (): Auth0ContextInterface["logout"] => {
  const { logout } = useAuth0();
  return useCallback(options => LogoutAction(options, logout), [logout]);
};

const AuthProvider: React.FC<AuthProviderProps> = ({ history, children }) => {
  const { authOClientId, auth0Domain, rootUrl } = useEnv();
  let initialScreen = parse(history.location.search)["initialScreen"];
  if (!(initialScreen === "login" || initialScreen === "signUp")) {
    initialScreen = "login";
  }
  const onRedirectCallback = (appState: any) => {
    // Use the router's history module to replace the url
    history.replace(appState?.returnTo || window.location.pathname);
  };

  return (
    <Auth0Provider
      domain={auth0Domain}
      clientId={authOClientId}
      redirectUri={window.location.origin + rootUrl + "/auth_callback"}
      onRedirectCallback={onRedirectCallback}
      initialScreen={initialScreen}
      useRefreshTokens={true}
      maxAge={43200}
      cacheLocation="localstorage"
    >
      {children}
    </Auth0Provider>
  );
};

export const defaultAuthOptions: WithAuthenticationRequiredOptions = {
  returnTo: () => {
    const { pathname, search, hash } = window.location ?? {
      pathname: "/",
      search: "",
      hash: ""
    };
    return [pathname, search, hash].join("");
  }
};
export { AuthProvider };
